import { IssueData, z } from 'zod';

const MAX_FILE_SIZE = 1 * 1024 * 1024; // 10MB
const ACCEPTED_IMAGE_TYPES = [
  // Image types
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/svg+xml"
];

const ACCEPTED_FILE_TYPES = [
  ...ACCEPTED_IMAGE_TYPES,

  // Document types
  "application/msword", // .doc
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/vnd.ms-excel", // .xls
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "text/csv", // .csv
  "application/pdf" // .pdf
];

export const step1Schema = z
  .object({
    name: z
      .string()
      .min(3, { message: 'Product name must be at least 3 characters long' })
      .max(200, { message: 'Product name must be at most 200 characters long' }),
    images: z
        .array(z.object({
          file: z.instanceof(File).superRefine((file, ctx) => {
            if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Invalid image type. Only .jpg, .jpeg, .png, .webp formats are supported.",
                params: { type: "invalid_type" },
              });
            }

            if (file.size > MAX_FILE_SIZE) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Each file must be less than 10MB.",
                params: { type: "too_large" },
              });
            }
          })
        }))
        .nullable(),
    files: z
        .array(z.object({
          file: z.instanceof(File).superRefine((file, ctx) => {
            if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Invalid file type. Only .jpg, .jpeg, .png, .webp, .svg, .doc, .docx, .xls, .xlsx, .csv, and .pdf formats are supported.",
                params: { type: "invalid_type" },
              });
            }

            if (file.size > MAX_FILE_SIZE) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Each file must be less than 10MB.",
                params: { type: "too_large" },
              });
            }
          })
        }))
        .nullable()
  })

  const productDetailSchema = {
    sales_price: z.number().nonnegative({ message: "Retail price must be a positive number." }).nullable(),
    cost: z.number().nonnegative({ message: "Product cost must be a positive number." }).nullable(),
    sku: z
      .string()
      .min(3, { message: 'SKU must be at least 3 characters long' })
      .max(200, { message: 'SKU must be at most 200 characters long' })
  }

  export const step2Schema = z.object(productDetailSchema)

  export const step2VartiantsSchema = z.array(z.object({
    ...productDetailSchema,
    stock:z.object({
      quantity: z.number().nonnegative( { message: "Inventory quantity must be a positive number." }).nullable(),
      reorder_point: z.number().nonnegative( { message: "Inventory re-order point must be a positive number." }).nullable(),
    }),
    image: z.object({
      file: z.instanceof(File).superRefine((file, ctx) => {
        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Invalid image type. Only .jpg, .jpeg, .png, .webp formats are supported.",
            params: { type: "invalid_type" },
          });
        }

        if (file.size > MAX_FILE_SIZE) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Each file must be less than 10MB.",
            params: { type: "too_large" },
          });
        }
      })
    })
    .nullable()
    .optional(),
  }))
